import { html } from "htm/preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { getClient } from "../client.js";
import { NotJoined } from "../components/NotJoined.js";
import { loadCourse } from "../courseData.js";
import { computeLiveRoundResults } from "../../../shared/leaderboard.js";
import { explainRoundBreakdown, type RoundBreakdown as Breakdown } from "../../../shared/explain.js";
import type { Contest } from "../../../shared/formats/types.js";
import { scoreTone } from "../util.js";
import { Icon } from "../components/Icon.js";
import type { Course } from "../../../shared/types.js";

/** A number as a contest cell, marked when that side took the hole. */
function SideCell({ value, won }: { value: number | null; won: boolean }) {
  return html`
    <td class=${won ? "grid-win" : ""}>
      ${value ?? "–"}${won && html`<span class="sr-only"> — won the hole</span>`}
    </td>
  `;
}

/** Sides that hold still all round get a column each, so holes read down the page. */
function FixedSidesTable({ contest }: { contest: Contest }) {
  const columns = contest.columns!;

  return html`
    <div class="table-scroll">
      <table class="scorecard-table contest-table">
        <thead>
          <tr>
            <th>Hole</th>
            ${columns.map((c) => html`<th key=${c.id}>${c.label}</th>`)}
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          ${contest.holes.map(
            (h) => html`
              <tr key=${h.hole} class=${h.sides.some((s) => s.won) ? "" : "row-empty"}>
                <td>${h.hole}</td>
                ${columns.map((col) => {
                  const side = h.sides.find((s) => s.id === col.id);
                  return html`<${SideCell}
                    key=${col.id}
                    value=${side?.value ?? null}
                    won=${Boolean(side?.won)}
                  />`;
                })}
                <td class="cell-note contest-result">${h.status ?? h.note ?? ""}</td>
              </tr>
            `,
          )}
        </tbody>
      </table>
    </div>
  `;
}

/**
 * Sides that re-form every hole (Wolf) can't be columns, and a table that carries their
 * names in a cell doesn't fit a phone: the side label, its score and the result all
 * competed for one row, so the score wrapped away from the name it belonged to and the
 * result ran off the right edge. A block per hole gives each side its own line with the
 * number pinned to the end of it, and never scrolls sideways.
 */
function ShiftingSidesList({ contest }: { contest: Contest }) {
  return html`
    <div class="contest-holes">
      ${contest.holes.map(
        (h) => html`
          <div class=${`contest-hole ${h.sides.some((s) => s.won) ? "" : "is-undecided"}`} key=${h.hole}>
            <div class="contest-hole-head">
              <span class="contest-hole-num">Hole ${h.hole}</span>
              ${(h.status ?? h.note) &&
              html`<span class="contest-hole-note">${h.status ?? h.note}</span>`}
            </div>
            ${h.sides.map(
              (s) => html`
                <div class=${`contest-side ${s.won ? "is-won" : ""}`} key=${s.id}>
                  <span class="contest-side-label">${s.label}</span>
                  <span class="contest-side-value">
                    ${s.value ?? "–"}${s.won && html`<span class="sr-only"> — won the hole</span>`}
                  </span>
                </div>
              `,
            )}
          </div>
        `,
      )}
    </div>
  `;
}

/** One contest, laid out according to whether its sides hold still. */
function ContestPanel({ contest }: { contest: Contest }) {
  return html`
    <div class="panel">
      <div class="panel-head">
        <span class="eyebrow">${contest.title}</span>
        <span class="chip chip-accent">${contest.standing}</span>
      </div>
      ${contest.columns
        ? html`<${FixedSidesTable} contest=${contest} />`
        : html`<${ShiftingSidesList} contest=${contest} />`}
      ${contest.columns &&
      contest.holes.some((h) => h.note) &&
      html`<p class="field-help mb-0">
        Holes with no winner were halved, not yet fully entered, or came after the match
        was already won.
      </p>`}
    </div>
  `;
}

/** One column's cell: the number it's judged on, and the balls behind it. */
function GridCell({
  cell,
  par,
  bySide,
  basis,
}: {
  cell: Breakdown["sections"][number]["rows"][number]["cells"][number];
  par: number;
  bySide: boolean;
  basis: Breakdown["basis"];
}) {
  // An individual column is one ball, so its gross *is* the headline and the net rides
  // beside it. A side's headline is the side's number, and the balls sit under it.
  const solo = !bySide && cell.members.length === 1;
  const member = cell.members[0];

  return html`
    <td class=${cell.best ? "grid-win" : ""}>
      ${solo
        ? html`
            <span class=${scoreTone(member?.gross ?? null, par)}>${member?.gross ?? "–"}</span>
            ${basis === "net" &&
            member?.net != null &&
            member.received > 0 &&
            html`<small class="grid-net">${member.net}</small>`}
          `
        : html`
            <span class="grid-side-value">${cell.value ?? "–"}</span>
            ${cell.members.length > 0 &&
            html`<small class="grid-side-balls">
              ${cell.members.map(
                (m, i) => html`
                  <span key=${m.playerId} class=${m.counts ? "grid-ball-counts" : ""}>
                    ${i > 0 ? " / " : ""}${m.gross ?? "–"}
                  </span>
                `,
              )}
            </small>`}
          `}
    </td>
  `;
}

/** Every column's card side by side, with the low score on each hole marked. */
function GridSection({
  section,
  breakdown,
  code,
}: {
  section: Breakdown["sections"][number];
  breakdown: Breakdown;
  code: string;
}) {
  const { bySide, basis, roundId } = breakdown;
  const anyGross = section.totals.some((t) => t.gross != null);
  // For a gross-basis individual round the value total *is* the gross total, so a second
  // row would just print it twice.
  const showValue = bySide || basis === "net";
  const partial = section.totals.some((t) => t.holesIn < section.holesTotal);

  return html`
    <div class="table-scroll">
      ${section.title && html`<p class="grid-section-title">${section.title}</p>`}
      <table class="scorecard-table grid-table">
        <thead>
          <tr>
            <th>Hole</th>
            <th>Par</th>
            <th>SI</th>
            ${section.columns.map(
              (col) => html`
                <th key=${col.id}>
                  ${col.playerIds.length === 1
                    ? html`<a href=${`#/t/${code}/round/${roundId}/player/${col.playerIds[0]}`}>
                        ${col.label}
                      </a>`
                    : col.label}
                </th>
              `,
            )}
          </tr>
        </thead>
        <tbody>
          ${section.rows.map(
            (row) => html`
              <tr key=${row.hole} class=${row.counted ? "" : "row-empty"}>
                <td>${row.hole}</td>
                <td>${row.par}</td>
                <td>${row.strokeIndex}</td>
                ${row.cells.map(
                  (cell) => html`<${GridCell}
                    key=${cell.columnId}
                    cell=${cell}
                    par=${row.par}
                    bySide=${bySide}
                    basis=${basis}
                  />`,
                )}
              </tr>
            `,
          )}
        </tbody>
        <tfoot>
          ${anyGross &&
          html`<tr>
            <th colspan="3">Gross</th>
            ${section.totals.map((t) => html`<td key=${t.columnId}>${t.gross ?? "–"}</td>`)}
          </tr>`}
          ${showValue &&
          html`<tr>
            <th colspan="3">${bySide ? "Total" : "Net"}</th>
            ${section.totals.map((t) => html`<td key=${t.columnId}>${t.value ?? "–"}</td>`)}
          </tr>`}
          <tr class="grid-points">
            <th colspan="3">Points</th>
            ${section.totals.map(
              (t) => html`<td key=${t.columnId}>${t.points ?? "—"}</td>`,
            )}
          </tr>
        </tfoot>
      </table>
    </div>
    ${partial &&
    html`<p class="field-help mb-0">
      ${`Totals cover the holes played so far, not all ${section.holesTotal}.`}
    </p>`}
  `;
}

function GridPanel({ breakdown, code }: { breakdown: Breakdown; code: string }) {
  const { bySide, basis, sections } = breakdown;
  const anyBalls = sections.some((s) => s.rows.some((r) => r.cells.some((c) => c.members.length > 1)));

  return html`
    <div class="panel">
      <div class="panel-head">
        <span class="eyebrow">${bySide ? "Sides, hole by hole" : "Hole by hole"}</span>
        <span class="chip">${basis === "gross" ? "low gross marked" : "low net marked"}</span>
      </div>
      ${sections.map(
        (section, i) => html`<${GridSection}
          key=${section.title ?? i}
          section=${section}
          breakdown=${breakdown}
          code=${code}
        />`,
      )}
      <p class="field-help mb-0">
        ${bySide
          ? anyBalls
            ? "Each side's score on the hole, with both players' gross underneath — the ball the side counted is the bold one."
            : "Each side plays one ball, so the hole is the side's score net of its combined handicap."
          : basis === "net"
            ? "Gross score, with the net beside it where handicap strokes apply."
            : "Gross score — this format is played off scratch."}
        ${" A hole is only marked once everyone on it has a score."}
      </p>
      ${breakdown.contests.length > 0 &&
      html`<p class="field-help mb-0">
        This is the plain card. The holes above are decided on the format's own terms,
        which can allocate strokes differently — so the two won't always agree.
      </p>`}
      ${!bySide &&
      html`<p class="field-help mb-0">Tap a name for that player's full breakdown.</p>`}
    </div>
  `;
}

/**
 * What each player took from the round.
 *
 * Only for a round whose sides change partway through: the points are paid per segment,
 * so the tables above show what each *side* earned over its own holes and none of them
 * says what a player went home with. Where there is one section, its footer already does.
 */
function RoundPointsPanel({ breakdown, code }: { breakdown: Breakdown; code: string }) {
  return html`
    <div class="panel">
      <span class="eyebrow">Points this round</span>
      <table class="scorecard-table mt-2">
        <tbody>
          ${breakdown.roundPoints.map(
            (p) => html`
              <tr key=${p.playerId}>
                <td>
                  <a href=${`#/t/${code}/round/${breakdown.roundId}/player/${p.playerId}`}>
                    ${p.name}
                  </a>
                </td>
                <td>${p.points}</td>
              </tr>
            `,
          )}
        </tbody>
      </table>
      <p class="field-help mb-0">
        Added up across the segments above. Tap a name for that player's full breakdown.
      </p>
    </div>
  `;
}

/**
 * The whole field, hole by hole — who won each hole rather than what one player scored.
 *
 * A sibling route to PlayerRound rather than a mode of it: the per-player card answers a
 * different question, and match play and Wolf are contests between sides that a
 * single-player card has nowhere to put.
 */
export function RoundBreakdown({ code, roundId }: { code: string; roundId: string }) {
  const client = getClient(code);
  const snapshot = client?.snapshot.value ?? null;
  const round = snapshot?.tournament.rounds.find((r) => r.id === roundId) ?? null;
  const [course, setCourse] = useState<Course | null>(null);

  useEffect(() => {
    if (round?.courseId) loadCourse(round.courseId).then(setCourse);
  }, [round?.courseId]);

  const breakdown = useMemo(() => {
    if (!snapshot || !round) return null;
    const courses = new Map<string, Course>();
    if (course) courses.set(course.id, course);
    const results = computeLiveRoundResults(
      snapshot.tournament,
      courses,
      snapshot.scores,
      snapshot.perHoleData,
    );
    const result = results.find((r) => r.roundId === round.id);
    if (!result) return null;
    return explainRoundBreakdown(
      round,
      course ?? undefined,
      snapshot.tournament.players,
      snapshot.scores,
      snapshot.perHoleData,
      result,
    );
  }, [snapshot, round, course]);

  if (!client) return html`<${NotJoined} code=${code} />`;
  if (!snapshot || !round || !breakdown) return html`<p class="muted">Loading…</p>`;

  const nameOf = (id: string) =>
    snapshot.tournament.players.find((p) => p.id === id)?.name ?? id;

  return html`
    <p class="back-link">
      <a href=${`#/t/${code}/leaderboard`}><${Icon} name="left" size=${16} /> Standings</a>
    </p>

    <h1 class="mb-0">${breakdown.roundName}</h1>
    <p class="course-line">
      ${breakdown.formatName}
      ${breakdown.courseName ? ` · ${breakdown.courseName}` : ""}
      ${breakdown.teeName ? ` (${breakdown.teeName})` : ""}
    </p>

    ${breakdown.provisional &&
    html`<p class="notice notice-ok">
      <span class="dot"></span> This round is still open, so nothing here is final yet.
    </p>`}

    ${breakdown.contests.map(
      (contest) => html`<${ContestPanel} key=${contest.id} contest=${contest} />`,
    )}

    ${breakdown.sections.some((s) => s.rows.length > 0) &&
    html`<${GridPanel} breakdown=${breakdown} code=${code} />`}

    ${breakdown.sections.length > 1 &&
    html`<${RoundPointsPanel} breakdown=${breakdown} code=${code} />`}

    <div class="panel">
      <span class="eyebrow">The rules being applied</span>
      <ul class="rule-list">
        ${breakdown.rules.map((line, i) => html`<li key=${i}>${line}</li>`)}
      </ul>
    </div>
  `;
}
